import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { CrudService } from 'src/core/services/crud/crud.service';
import { CampaignSummaryDto } from '../models/campaign-summary.dto';
import { CampaignReservationResultDto } from '../models/campaign-reservation-result.dto';
import { CampaignBeginCallResultDto } from '../models/campaign-begin-call-result.dto';
import { CampaignAssignedWrapUpCode } from '../models/campaign-assigned-wrap-up-code.model';

const AVAILABLE_SUFFIX = 'available';
const RESERVE_NEXT_SUFFIX = 'reserve-next';
const RELEASE_SUFFIX = 'release';
const BEGIN_CALL_SUFFIX = 'begin-call';
const ASSIGNED_WRAP_UP_CODES_SUFFIX = 'assigned-wrap-up-codes';

/**
 * Lightweight HTTP client for the agent-facing campaign-execution endpoints. Separate from the admin
 * feature CampaignService (configuration/CRUD): this client owns only reservation and progress reads.
 */
@Injectable({ providedIn: 'root' })
export class CampaignOutboundClientService {
  private readonly crudService = inject(CrudService);

  /**
   * Returns the Active campaigns the current agent is eligible to run via queue membership.
   * @returns The available campaigns with their reservable contact counts.
   */
  getAvailableCampaigns(): Observable<CampaignSummaryDto[]> {
    return this.crudService.getWithSuffix<CampaignSummaryDto[]>(CampaignSummaryDto, AVAILABLE_SUFFIX);
  }

  /**
   * Reserves the next eligible contact of a campaign for the current agent (server-side advance).
   * @param campaignId Identifier of the campaign to reserve from.
   * @returns The reservation result including the reserved contact when successful.
   */
  reserveNextContact(campaignId: number): Observable<CampaignReservationResultDto> {
    return this.crudService.insertWithoutId<CampaignReservationResultDto>(
      CampaignSummaryDto,
      { campaignId } as unknown as CampaignReservationResultDto,
      RESERVE_NEXT_SUFFIX,
    );
  }

  /**
   * Releases the current agent's reservation on a campaign-contact row.
   * @param campaignContactId Identifier of the reserved campaign-contact row.
   * @returns A stream that completes when the release attempt has been processed.
   */
  releaseReservation(campaignContactId: number): Observable<void> {
    return this.crudService.insertWithoutId<void>(
      CampaignSummaryDto,
      { campaignContactId } as unknown as void,
      RELEASE_SUFFIX,
    );
  }

  /**
   * Transitions a reserved campaign-contact row to Calling for the current agent (Reserved -> Calling +
   * CallStartedAtUtc), the server-side keystone the success seam and reservation-expiry sweep key off.
   * @param campaignContactId Identifier of the reserved campaign-contact row.
   * @returns The begin-call result; started is false when the reservation had already expired or moved.
   */
  beginCall(campaignContactId: number): Observable<CampaignBeginCallResultDto> {
    return this.crudService.insertWithoutId<CampaignBeginCallResultDto>(
      CampaignSummaryDto,
      { campaignContactId } as unknown as CampaignBeginCallResultDto,
      BEGIN_CALL_SUFFIX,
    );
  }

  /**
   * Returns the wrap-up codes assigned to a campaign in its configuration (system codes excluded server-side),
   * used to populate the ACW wrap-up selector when the current engagement is a campaign call. Reads from the
   * campaign configuration route `api/agentic-cx/campaign/assigned-wrap-up-codes/{campaignId}`.
   * @param campaignId Identifier of the campaign whose assigned wrap-up codes are requested.
   * @returns The wrap-up codes assigned to the campaign, each with its id, display name, and outcome policy.
   */
  getAssignedWrapUpCodes(campaignId: number): Observable<CampaignAssignedWrapUpCode[]> {
    return this.crudService.getWithSuffix<CampaignAssignedWrapUpCode[]>(
      CampaignAssignedWrapUpCode,
      `${ASSIGNED_WRAP_UP_CODES_SUFFIX}/${campaignId}`,
    );
  }
}
